import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import DataAndMap, { surfaceTypes } from "./DataAndMap";
import { AxiosContext } from "../../services/axios/AxiosContext";
// eslint-disable-next-line no-restricted-imports
import axios from "axios";
import {
  buildDefaultSurfacesVisibility,
  SurfacesVisibility,
} from "./contexts/DataContext";
import { SimpleLatLng } from "./Core";
import SnowScapeLogoWithWhiteText from "../../resources/logo/snowscape-logo-horizontal-with-white-text.svg";
import { SeasonName } from "../../services/axios/jobs";
import EditStateProvider from "./contexts/EditStateContext";
import { PaywallProviderSkip } from "./contexts/PaywallContext";

import { RenderStage, isWaitingForMap } from "./utils/renderStage";

declare global {
  interface Window {
    __SNOWSCAPE_RENDER_STAGE__?: RenderStage;
    gm_authFailure?: () => void;
  }
}

export interface RenderSiteMapState {
  surfacesVisibility?: SurfacesVisibility;
  zoom: number;
  center: SimpleLatLng;
}

/**
 * A wrapper around all embedded content that takes care of receiving the token info and setting up axios interceptors / current user data.
 */
function RenderSiteMap() {
  const searchParams = useSearchParams()[0];
  const rawState = searchParams.get("state") ?? "{}";
  const state = useMemo(
    () => JSON.parse(rawState) as RenderSiteMapState,
    [rawState],
  );
  const { season } = useParams<{ season: SeasonName }>();

  // If we have not been given a surface visibility object, default to everything minus property boundary.
  const surfacesVisibility = useMemo(() => {
    let vis = state.surfacesVisibility;
    if (vis == null) {
      vis = buildDefaultSurfacesVisibility(surfaceTypes[season!]);
      vis.boundary = false;
    }
    return vis;
  }, [state.surfacesVisibility, season]);

  const key = searchParams.get("key");

  const [finishedRendering, setFinishedRendering] = useState(false);
  const [renderStage, setRenderStage] = useState<RenderStage>("loading_data");

  // Keep window global updated for tests and headless diagnostics
  useEffect(() => {
    window.__SNOWSCAPE_RENDER_STAGE__ = renderStage;
  }, [renderStage]);

  // Catch Google Maps authentication failures
  useEffect(() => {
    const prevAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      setRenderStage("error_maps_auth");
      if (typeof prevAuthFailure === "function") {
        prevAuthFailure();
      }
    };
    return () => {
      window.gm_authFailure = prevAuthFailure;
    };
  }, []);

  // Setup an Axios instance with stable memoization so DataAndMap never aborts and re-fetches
  const sharedAuthAxios = useMemo(() => {
    const apiBaseUrl =
      process.env.REACT_APP_BACKEND_API ?? "https://api.mysnowscape.com/v2/";

    const formattedBaseUrl = apiBaseUrl.endsWith("/")
      ? apiBaseUrl
      : `${apiBaseUrl}/`;

    const instance = axios.create({
      withCredentials: true,
      baseURL: formattedBaseUrl,
    });
    instance.defaults.withCredentials = true;
    instance.interceptors.request.use(async function (config) {
      config.headers.Authorization = key;
      return config;
    });
    return instance;
  }, [key]);

  const onMapReady = useCallback(() => {
    setRenderStage("tiles_loaded");
    setFinishedRendering(true);
  }, []);

  const handleStageChange = useCallback((stage: RenderStage) => {
    setRenderStage(stage);
  }, []);

  const handleRenderReady = useCallback(() => {
    setRenderStage("map_render_ready");
  }, []);

  const dataAndMap = useMemo(
    () => (
      <AxiosContext.Provider value={sharedAuthAxios}>
        <PaywallProviderSkip>
          <EditStateProvider>
            <DataAndMap
              disableUi={true}
              surfacesVisibility={surfacesVisibility}
              fullScreen={true}
              onMapReady={onMapReady}
              onStageChange={handleStageChange}
              center={state.center}
              zoom={state.zoom}
            />
            <div
              style={{
                position: "absolute",
                top: 5,
                left: 5,
                zIndex: 1000,
                backgroundColor: "rgba(0,0,0,.5)",
                borderRadius: "10px",
                padding: 4,
              }}
            >
              <img
                style={{ width: "70px" }}
                alt="Made With SnowScape"
                src={SnowScapeLogoWithWhiteText}
              />
            </div>
          </EditStateProvider>
        </PaywallProviderSkip>
      </AxiosContext.Provider>
    ),
    [
      surfacesVisibility,
      onMapReady,
      handleStageChange,
      sharedAuthAxios,
      state.center,
      state.zoom,
    ],
  );

  return (
    <>
      {dataAndMap}
      {/* Explicit render stage tracking so tests and observers know we are waiting for the map */}
      <div
        id="render-stage"
        data-stage={renderStage}
        data-waiting-for-map={isWaitingForMap(renderStage)}
        style={{ display: "none" }}
      />
      {/* Render a hidden element here once everything is ready. This allows CloudFlare to notice when things are ready to screenshot. */}
      <FinishRenderingElement
        show={finishedRendering}
        onReady={handleRenderReady}
      />
    </>
  );
}

/**
 * A component that renders a "ready" div after a delay.
 * Uses a ref for onReady so parent re-renders can never prematurely clear the timer.
 * Emits an offscreen layout element so Puppeteer waitForSelector reliably detects it.
 */
function FinishRenderingElement({
  show,
  onReady,
}: {
  show: boolean;
  onReady?: () => void;
}) {
  const [internalShow, setInternalShow] = useState(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    if (!show || internalShow) return;
    const timeoutId = window.setTimeout(() => {
      setInternalShow(true);
      onReadyRef.current?.();
    }, 800);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [show, internalShow]);

  if (internalShow) {
    return (
      <div
        id="map-render-ready"
        data-ready="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "1px",
          height: "1px",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
      />
    );
  }
  return null;
}

export default RenderSiteMap;
